import { AxiosError } from 'axios';
import {
  isNetworkError,
  isRotatedElsewhere,
  isSessionOver,
  refreshLock,
  RefreshLock,
  tokenService,
} from '@root/v2/api';
import { ErrorResult } from '@root/v2/types';
import {
  loginApi,
  verifyTFA as verifyTFAApi,
  recoverTFA as recoverTFAApi,
  getTFASetupData,
  confirmTFA,
  devLoginApi,
  initiateK1LoginApi,
  exchangeK1CodeApi,
  logoutApi,
  refreshTokensApi,
  completeK1LogoutApi,
  forgotPasswordApi,
  verifyResetTokenApi,
  setPasswordApi,
} from '../api';
import {
  K1_LOGIN_FAILED_MESSAGE,
  K1_LOGIN_REQUIRED_CODE,
  K1_LOGOUT_FAILED_MESSAGE,
  TFA_SESSION_EXPIRED_CODE,
  TFA_SESSION_EXPIRED_MESSAGE,
} from '../constants';
import { AuthTokensResponse, LoginApiError, SetPasswordApiError } from '../types';
import {
  DevLoginResult,
  K1CallbackResult,
  K1RedirectResult,
  ForgotPasswordResult,
  SetPasswordResult,
  VerifyResetTokenResult,
  LoginResult,
  TFAConfirmResult,
  TFAErrorResult,
  TFASetupResult,
  VerifyResult,
} from './types';

const handleApiError = (error: unknown, fallbackMessage: string): ErrorResult => {
  const axiosError = error as AxiosError<LoginApiError>;

  if (axiosError.response) {
    const { data } = axiosError.response;

    return { success: false, errorMessage: data?.message || fallbackMessage };
  }

  return { success: false, errorMessage: 'Network error. Please check your connection.' };
};

let inMemoryTFAToken: string | null = null;

const tokenStorage = {
  setTFAToken: (token: string) => {
    inMemoryTFAToken = token;
  },

  getTFAToken: (): string | null => inMemoryTFAToken,

  clearTFAToken: () => {
    inMemoryTFAToken = null;
  },

  startPendingTFA: (token: string) => {
    tokenService.clearTokens();
    tokenStorage.setTFAToken(token);
  },

  setAuthTokens: (data: AuthTokensResponse) => {
    tokenService.setTokens(data);
  },

  clearAll: () => {
    tokenStorage.clearTFAToken();
    tokenService.clearTokens();
  },
};

const UNAUTHORIZED_STATUS = 401;

const TFA_SESSION_OVER_RESULT: TFAErrorResult = {
  success: false,
  errorMessage: TFA_SESSION_EXPIRED_MESSAGE,
  isTFASessionOver: true,
};

const endsTFASession = (error: unknown): boolean => {
  const { response } = error as AxiosError<LoginApiError>;

  return response?.status === UNAUTHORIZED_STATUS && response.data?.code === TFA_SESSION_EXPIRED_CODE;
};

const handleTFAError = (error: unknown, fallbackMessage: string): TFAErrorResult => {
  const isTFASessionOver = endsTFASession(error);

  if (isTFASessionOver) {
    tokenStorage.clearTFAToken();
  }

  return { ...handleApiError(error, fallbackMessage), isTFASessionOver };
};

export const login = async (email: string, password: string): Promise<LoginResult> => {
  try {
    const response = await loginApi({ email, password });

    if (response.tfa_token) {
      tokenStorage.startPendingTFA(response.tfa_token);

      return { success: true, requiresTFA: true };
    }

    return { success: false, errorMessage: 'Unexpected response from server' };
  } catch (error) {
    const axiosError = error as AxiosError<LoginApiError>;

    if (axiosError.response) {
      const { status, data } = axiosError.response;

      // 403 with tfa_token means TFA setup is required
      if (status === 403 && data?.tfa_token) {
        tokenStorage.startPendingTFA(data.tfa_token);

        return { success: true, requiresTFASetup: true };
      }

      // 403 with this code means the organization only allows signing in via KaseyaOne
      if (status === 403 && data?.code === K1_LOGIN_REQUIRED_CODE) {
        return { success: true, requiresK1Login: true };
      }

      // 401/422 - invalid credentials
      if (status === 401 || status === 422) {
        return { success: false, errorMessage: data?.message || 'Invalid email or password' };
      }

      return { success: false, errorMessage: data?.message || 'Something went wrong' };
    }

    return { success: false, errorMessage: 'Network error. Please check your connection.' };
  }
};

export const verifyTFA = async (code: string): Promise<VerifyResult> => {
  const tfaToken = tokenStorage.getTFAToken();

  if (!tfaToken) {
    return TFA_SESSION_OVER_RESULT;
  }

  try {
    const result = await verifyTFAApi(tfaToken, code);
    tokenStorage.setAuthTokens(result);
    tokenStorage.clearTFAToken();

    return { success: true };
  } catch (error) {
    return handleTFAError(error, 'Invalid code');
  }
};

export const recoverTFA = async (code: string): Promise<VerifyResult> => {
  const tfaToken = tokenStorage.getTFAToken();

  if (!tfaToken) {
    return TFA_SESSION_OVER_RESULT;
  }

  try {
    const result = await recoverTFAApi(tfaToken, code);
    tokenStorage.setAuthTokens(result);
    tokenStorage.clearTFAToken();

    return { success: true };
  } catch (error) {
    return handleTFAError(error, 'The entered code is incorrect.');
  }
};

export const getTFASetup = async (): Promise<TFASetupResult> => {
  const tfaToken = tokenStorage.getTFAToken();

  if (!tfaToken) {
    return { success: false, errorMessage: TFA_SESSION_EXPIRED_MESSAGE };
  }

  try {
    const response = await getTFASetupData(tfaToken);
    tokenStorage.setTFAToken(response.tfa_token);

    return {
      success: true,
      secret: response.secret,
      qrCode: response.qr_code,
    };
  } catch (error) {
    return handleTFAError(error, 'Failed to load TFA setup data');
  }
};

export const confirmTFASetup = async (code: string): Promise<TFAConfirmResult> => {
  const tfaToken = tokenStorage.getTFAToken();

  if (!tfaToken) {
    return TFA_SESSION_OVER_RESULT;
  }

  try {
    const response = await confirmTFA(tfaToken, code);
    tokenStorage.setTFAToken(response.tfa_token);

    return { success: true, recoveryCodes: response.recovery_codes };
  } catch (error) {
    return handleTFAError(error, 'Invalid code');
  }
};

export const initiateK1Login = async (): Promise<K1RedirectResult> => {
  try {
    const { redirect_url: redirectUrl } = await initiateK1LoginApi();

    return { success: true, redirectUrl };
  } catch (error) {
    return handleApiError(error, 'Could not start KaseyaOne sign-in. Please try again.');
  }
};

export const handleK1Callback = async (code: string, state: string): Promise<K1CallbackResult> => {
  try {
    const result = await exchangeK1CodeApi(code, state);
    tokenStorage.setAuthTokens(result);

    return { success: true };
  } catch (error) {
    return handleApiError(error, K1_LOGIN_FAILED_MESSAGE);
  }
};

/**
 * Completes a KaseyaOne single logout from the /v2/logout/callback page: hands the state KaseyaOne
 * echoed back to the backend, which blacklists the tokens filed when the logout began.
 */
export const handleK1LogoutCallback = async (state: string): Promise<K1CallbackResult> => {
  try {
    await completeK1LogoutApi(state);

    return { success: true };
  } catch (error) {
    return handleApiError(error, K1_LOGOUT_FAILED_MESSAGE);
  }
};

export const devLogin = async (token: string): Promise<DevLoginResult> => {
  try {
    const result = await devLoginApi(token);
    tokenStorage.setAuthTokens(result);

    return { success: true };
  } catch (error) {
    return handleApiError(error, 'Login failed');
  }
};

export const forgotPassword = async (email: string): Promise<ForgotPasswordResult> => {
  try {
    await forgotPasswordApi(email);

    return { success: true };
  } catch (error) {
    return handleApiError(error, 'Unable to send reset instructions. Please try again.');
  }
};

export const verifyResetToken = async (token: string): Promise<VerifyResetTokenResult> => {
  try {
    await verifyResetTokenApi(token);

    return { status: 'valid' };
  } catch (error) {
    if (isNetworkError(error)) {
      return { status: 'network' };
    }

    const status = (error as AxiosError).response?.status;

    if (status && status >= 500) {
      return { status: 'error' };
    }

    return { status: 'expired' };
  }
};

export const setPassword = async (
  token: string,
  password: string,
  confirmPassword: string,
): Promise<SetPasswordResult> => {
  try {
    await setPasswordApi(token, password, confirmPassword);

    return { status: 'success' };
  } catch (error) {
    const { response } = error as AxiosError<SetPasswordApiError>;

    if (response?.status === 422) {
      const { errors } = response.data ?? {};

      if (errors) {
        const [firstError] = Object.values(errors).flat();

        return { status: 'invalid', errorMessage: firstError ?? 'Password does not meet the requirements.' };
      }

      return { status: 'expired' };
    }

    return { status: 'error', errorMessage: 'Something went wrong. Please try again.' };
  }
};

/**
 * Drops the local half of a session without telling the backend, for flows abandoned before a session
 * exists and there is anything on the server to end.
 */
export const clearSession = (): void => {
  tokenStorage.clearAll();
};

/**
 * Ends the session everywhere it exists. Only KaseyaOne can end its own, so the browser has to visit
 * the URL the backend answers with, or the user is signed straight back in.
 * Navigates directly because that URL is cross-origin, and carries "/v2" inside its redirect
 * parameter - which useRedirect would read as a local route.
 */
export const logout = async (): Promise<string | undefined> => {
  const accessToken = tokenService.getAccessToken();
  const refreshToken = tokenService.getRefreshToken();
  let k1LogoutUrl: string | undefined;

  if (accessToken && refreshToken) {
    try {
      ({ k1_logout_url: k1LogoutUrl } = await logoutApi(accessToken, refreshToken));
    } catch (error) {
      // Nothing here can keep the user signed in, so a failure must not strand them on the page.
      // Log only the status and message - the error object carries the bearer token in its config.
      const axiosError = error as AxiosError;
      // eslint-disable-next-line no-console -- surfaces logout failures without the token-bearing error object
      console.error('Logout request to the server failed', {
        status: axiosError.response?.status,
        message: axiosError.message,
      });
    }
  }

  clearSession();

  return k1LogoutUrl;
};

export const hasTFAToken = (): boolean => Boolean(tokenStorage.getTFAToken());

const waitForOtherRefresher = async (): Promise<boolean> => {
  try {
    await refreshLock.waitForRefresh();

    return true;
  } catch {
    return false;
  }
};

export const refreshSession = async (): Promise<boolean> => {
  const accessToken = tokenService.getAccessToken();
  const refreshToken = tokenService.getRefreshToken();

  if (!accessToken || !refreshToken || tokenService.isRefreshTokenExpired()) {
    tokenService.clearTokens();

    return false;
  }

  if (RefreshLock.wasRecentlyRefreshed()) {
    return true;
  }

  if (RefreshLock.isLocked() || !RefreshLock.acquire()) {
    await waitForOtherRefresher();

    if (isRotatedElsewhere(refreshToken)) {
      return true;
    }

    if (tokenService.isRefreshTokenExpired()) {
      tokenService.clearTokens();
    }

    return false;
  }

  try {
    const tokens = await refreshTokensApi(accessToken, refreshToken);
    tokenService.setTokens(tokens);
    RefreshLock.setLastRefreshTime();
    refreshLock.release(true);

    return true;
  } catch (error) {
    refreshLock.release(false);

    const isRefreshRejected = isSessionOver(error);

    if (isRefreshRejected && RefreshLock.isLocked()) {
      await waitForOtherRefresher();
    }

    if (isRotatedElsewhere(refreshToken)) {
      return true;
    }

    if (isRefreshRejected) {
      tokenService.clearTokens();
    }

    return false;
  }
};
